import React, { FunctionComponent } from 'react';
import Image from 'next/image';
import { Box, Card, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { useRouter } from 'next/router';
import { InnerLink } from '../../InnerLink';
import { clickTheSeeAllResultsLink } from '@wix/bi-logger-new-help-center/v2';
import { DATA_HOOKS } from '../../../dataHooks';
import { EDITORS_NAMES } from '../../../constants';
import { mapPageTypeToSourceName } from '../../../utils/bi';
import { ArticleSearchResultItem, PageType } from '../../../types';
import { useBI } from '../../../hooks/useBI';
import css from '../index.module.scss';
import { RenderHTML } from '../../RenderHTML';

type DeprecatedSearchBarResultsProps = {
  articles: ArticleSearchResultItem[];
  inputText: string;
  itemId?: string;
  itemType?: string;
  searchSessionId: string;
  pageType: PageType;
  onClear: () => void;
  focusedArticleIndex: number;
  setFocusedArticleIndex: (index: number) => void;
  onSearchResultClick: (
    article: ArticleSearchResultItem,
    index: number
  ) => Promise<void>;
};

/**
 * @deprecated use 'components/SearchBar/SearchBarResults/index.tsx' instead
 */
export const DeprecatedSearchBarResults: FunctionComponent<
  DeprecatedSearchBarResultsProps
> = ({
  articles,
  inputText,
  itemId,
  itemType,
  searchSessionId,
  pageType,
  onClear,
  focusedArticleIndex,
  setFocusedArticleIndex,
  onSearchResultClick,
}) => {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const { sendBIEvent } = useBI();

  const getArticleTitle = (title: string) => {
    if (title.includes(':')) {
      const [firstPart, secondPart] = title.split(':');
      const isEditor = EDITORS_NAMES.some((editor) =>
        firstPart.includes(editor)
      );
      if (isEditor) {
        return (
          <>
            <b>{firstPart}</b>:{secondPart}
          </>
        );
      }
    }
    return title;
  };

  const onShowAllSearchResultsClick = async (): Promise<void> => {
    await sendBIEvent(
      clickTheSeeAllResultsLink({
        source_name: mapPageTypeToSourceName(pageType),
        item_id: itemId,
        item_type: itemType,
        kb_lang: locale as string,
        question: inputText,
        search_session_Id: searchSessionId,
      })
    );
    onClear();
  };

  return (
    <Box dataHook={DATA_HOOKS.SEARCH_BAR_RESULTS} direction="vertical">
      <Box className={css.results} direction="vertical">
        {articles.map((article: ArticleSearchResultItem, index) => (
          <InnerLink
            href={article.uri}
            className={css.resultLink}
            key={`${article.id}_${index}`}
            onClick={() => onSearchResultClick(article, index)}
            dataHook={DATA_HOOKS.SEARCH_RESULT}
            onMouseEnter={() => setFocusedArticleIndex(index)}
          >
            <Box
              gap="10px"
              className={`${css.result} ${
                focusedArticleIndex === index && css.keyBoardFocus
              }`}
              verticalAlign="middle"
            >
              <Image
                src={`https://static.parastorage.com/services/kb-client-nextjs/dist/public/images/icons/document.svg`}
                height="31"
                width="24"
                alt=""
              />
              <Box direction="vertical" maxWidth="calc(100% - 48px)">
                <Text
                  disabled
                  ellipsis
                  size="small"
                  dataHook={DATA_HOOKS.SEARCH_RESULT_TITLE}
                  className={css.resultText}
                >
                  {getArticleTitle(article.title)}
                </Text>

                {
                  <RenderHTML
                    html={article.description}
                    textElement={{
                      className: css.resultText,
                      ellipsis: true,
                      size: 'small',
                      disabled: true,
                      dataHook: DATA_HOOKS.SEARCH_RESULT_DESCRIPTION,
                    }}
                  />
                }
              </Box>
            </Box>
          </InnerLink>
        ))}
      </Box>
      <Card.Divider />
      <Box
        className={`${css.seeMoreResultsWrapper} ${
          focusedArticleIndex === articles.length && css.keyBoardFocus
        }`}
      >
        <InnerLink
          href={`/search?term=${inputText}`}
          className={css.seeMoreResults}
          dataHook={DATA_HOOKS.SHOW_ALL_SEARCH_RESULTS}
          onClick={onShowAllSearchResultsClick}
          onMouseEnter={() => setFocusedArticleIndex(articles.length)}
        >
          <Text size="small">
            {t('searchbar.see.all.results')} <b>‘{inputText}’</b>
          </Text>
        </InnerLink>
      </Box>
      <div aria-live="assertive" key={inputText} className={css.srOnly}>
        {t(`searchbar.results.reader`, { count: articles.length })}
      </div>
    </Box>
  );
};
